import { Injectable } from '@angular/core';
import { BillingType, IOrderPrices, IOrderResponse, IOrderUpdate } from './order-entry-data.service';
import { OrderEntryLocationDataService, OrderEntryContactType, OrderEntryAddressType, LocationProperty } from './location/order-entry-location-data.service';
import { OrderEntryInfoDataService } from './order-info/order-entry-info-data.service';
import { OrderEntryRequesterService } from './requester/order-entry-requester.service';
import { OrderEntryInfoService } from './order-info/order-entry-info.service';
import { OrderEntryPaymentService } from './payment/order-entry-payment.service';
import { OrderEntryInvoiceService } from './location/invoice/order-entry-invoice.service';
import { OrderEntryShippingService } from './location/shipping/order-entry-shipping.service';
import { ICartItemStatus, ICatalogCartItem, IPunchoutCartItem, PriceCartService } from '../cart/price-cart.service';
import { OrderEntryChargesService } from './charges/order-entry-charges.service';
import { OrderEntryDocumentsService } from './documents/order-entry-documents.service';
import { OrderEntryDocumentsDataService } from './documents/order-entry-documents-data.service';
import { OrderEntryConfirmService } from './confirm/order-entry-confirm.service';
import { OrderEntryWebshopService } from './webshop/order-entry-webshop.service';
import { FormattingService } from '../../../shared/services/formatting.service';
import * as _ from 'underscore';
import moment from 'moment';
import { BaseOrderEntryLocationToService, IAddressData, IContactModel } from './location/base-order-entry-location-to.service';
import { diffUtil, util } from '../../../shared/util';
import { ACTION_CODE_SUBMIT } from './order-entry.constants';
import jsonpatch from 'fast-json-patch';
import { IContact, IObject } from '../../../../type_definitions/datatypes';

@Injectable()
export class OrderEntryService {
  private _order:IOrderResponse;
  private _originalOrder:IOrderUpdate;
  private _orderNumber:number;
  private _checkoutFunction:Function;
  private _requesterRequired:boolean;
  private _orderEditable:boolean;
  private _noMailNotifications:boolean;
  private _orderStatus:string;
  private _stockOrderTypeId:string;

  constructor(
    private orderEntryLocationDataService:OrderEntryLocationDataService,
    private orderEntryInfoDataService:OrderEntryInfoDataService,
    private orderEntryRequesterService:OrderEntryRequesterService,
    private orderEntryInfoService:OrderEntryInfoService,
    private orderEntryPaymentService:OrderEntryPaymentService,
    private orderEntryInvoiceService:OrderEntryInvoiceService,
    private orderEntryShippingService:OrderEntryShippingService,
    private priceCartService:PriceCartService,
    private orderEntryChargesService:OrderEntryChargesService,
    private orderEntryDocumentsService:OrderEntryDocumentsService,
    private orderEntryDocumentsDataService:OrderEntryDocumentsDataService,
    private orderEntryConfirmService:OrderEntryConfirmService,
    private orderEntryWebshopService:OrderEntryWebshopService,
    private formattingService:FormattingService
  ) {
    /***/
  }
  
  public set order(order:IOrderResponse) {
    this._order = order;
    this._orderNumber = order.id;
    this._orderStatus = order.orderStatusDescription;
    this.initModels(order);
    this._originalOrder = _.clone(this.prepareOrderForSave());
  }

  public get order():IOrderResponse {
    return this._order;
  }

  private initModels(order:IOrderResponse):void {
    if (order.requester) {
      this.orderEntryRequesterService.contact = _.pick(order.requester, [
        'id',
        'fullName',
        'firstname',
        'lastname',
        'email',
        'directPhone'
      ]);
      if (order.requester.address && order.requester.address.location) {
        this.orderEntryRequesterService.address = order.requester.address; //do not use the address without location
      }
    }

    this.orderEntryInfoService.orderCreationDate = order.orderCreationDate;
    this.orderEntryInfoService.description = order.orderDescription;
    this.orderEntryInfoService.referenceCustomer = order.customerReference;
    this.orderEntryInfoService.customerPONumber = order.customerPONumber;
    if (order.requestedArrivalDate) {
      this.orderEntryInfoService.arrivalDate = moment(order.requestedArrivalDate).toDate();
    }
    this.orderEntryInfoService.tagValue = order.tagValue;
    this.orderEntryInfoService.tagValue2 = order.tagValue2;
    this.orderEntryInfoService.orderType = order.orderType;
    this.orderEntryInfoService.costCenter = order.costCenter;
    this.orderEntryInfoService.stockOrderType = order.stockOrderType;
    this.orderEntryInfoService.comment = order.comment;

    if (order.payment?.paymentType) {
      this.orderEntryPaymentService.paymentType = order.payment.paymentType;
    }
    this.priceCartService.prices = <IOrderPrices>(this.orderEntryWebshopService.showOnlyLeasingPrice ? order.leasePrices : order.purchasePrices);

    if (order.orderCurrency) {
      this.priceCartService.currency = order.orderCurrency.isoCode;
    }
    this.priceCartService.proposeAlternativesOption(order.proposeAlternatives);

    _.forEach({
      shipto: this.orderEntryShippingService,
      invoiceto: this.orderEntryInvoiceService
    }, (locationToModel:BaseOrderEntryLocationToService, key:string) => {
      if (order[ key ]) {
        locationToModel.selectedAddressType = { id: order[ key ].addressType };
        if (order[ key ].location) {
          locationToModel.selectedAddressType = { id: OrderEntryAddressType.CURRENT_ADDRESS };
          const addressModel:IAddressData = <IAddressData>{};
          util.setValue(addressModel, 'location', order[ key ].location, true);
          util.setValue(addressModel, 'country', util.getValue(order[ key ].location, 'companyCountry.country'), true);
          util.setValue(addressModel, 'state', order[ key ].location.state, true);
          util.setValue(addressModel, 'company', util.getValue(order[ key ].location, 'companyCountry.company.name'), true);
          util.setValue(addressModel, 'address', order[ key ].location.address, true);
          util.setValue(addressModel, 'zip', order[ key ].location.zipCode, true);
          util.setValue(addressModel, 'city', order[ key ].location.city, true);
          util.setValue(addressModel, 'tax', order[ key ].location.vatNumber, true);
          util.setValue(addressModel, 'vatName', undefined, true);
          util.setValue(addressModel, 'office', order[ key ].office, true);
          if (key === LocationProperty.SHIP_TO) {
            util.setValue(addressModel, 'defaultAddress', order[ key ][ 'default' ], true);
          }
          locationToModel.addressData = addressModel;
        }

        if (order[ key ].contact) {
          locationToModel.contactType = { id: OrderEntryContactType.CURRENT_CONTACT };
          locationToModel.contactData = {
            contact: order[ key ].contact,
            id: order[ key ].contact.id,
            fullName: order[ key ].contact.fullName,
            email: order[ key ].contact.email,
            directPhone: order[ key ].contact.directPhone
          };
          locationToModel.eInvoiceMailAddress = order[ key ].contact.email;
        }
        locationToModel.comments = order[ key ].comment;
        locationToModel.overnight = order[ key ].overnightShipment;
        if (key === LocationProperty.INVOICE_TO) {
          locationToModel.billingType = { id: order.billingType };
        }
      }
    });

    this.priceCartService.cartStatus = _.map(order.items, (item:ICatalogCartItem | IPunchoutCartItem) => {
      const itemId:number | string = this.priceCartService.getItemId(item);
      const cartStatus:ICartItemStatus = <any>{
        itemId,
        id: (<ICatalogCartItem>item).productId,
        count: item.quantity,
        cartCount: item.quantity,
        product: {
          itemId,
          id: (<ICatalogCartItem>item).productId,
          productId: (<ICatalogCartItem>item).productId,
          sourceType: item.sourceType,
          orderLineNumber: (<ICatalogCartItem>item).id,
          orderLineStatus: item.orderLineStatus,
          orderLineStatusDescription: item.orderLineStatusDescription,
          part: (<IPunchoutCartItem>item).part,
          partConfigurationExist: (<ICatalogCartItem>item).partConfigurationExist,
          partSuffix: (<ICatalogCartItem>item).partSuffix,
          partSuffixDescription: (<ICatalogCartItem>item).partSuffixDescription,
          partSuffixFullDescription: this.priceCartService.formatPartDescription(this.priceCartService.getPartConfigurationFromProduct(<ICatalogCartItem>item), 'ORDERENTRY.CART.PART_CONFIGURATION.KEYBOARD'),
          inValid: (this._orderStatus !== 'PLACED' && (item.orderLineStatus === 'NA' || item.orderLineStatus === 'EOL')),
          price: {
            amount: item.quantity * (item.unitPrice || item.initialUnitPrice)
          },
          unitPrice: item.unitPrice,
          initialUnitPrice: item.initialUnitPrice,
          unitLeasePrice: item.quantity * <number>item.unitLeasePrice,
          unitLeasePriceTotal: item.quantity * <number>item.unitLeasePriceTotal,
          leasePeriod: item.leasePeriod,
          extUnitPrice: (<IPunchoutCartItem>item).extUnitPrice,
          extCurrency: (<IPunchoutCartItem>item).extCurrency,
          title: (<IPunchoutCartItem>item).description,
          description: (<IPunchoutCartItem>item).description,
          shortDescription: (<IPunchoutCartItem>item).shortDescription || (<IPunchoutCartItem>item).description,
          vendorId: (<IPunchoutCartItem>item).vendorId,
          catalogId: (<IPunchoutCartItem>item).catalogId,
          category: (<IPunchoutCartItem>item).category,
          taxPercentage: (<ICatalogCartItem>item).taxPercentage? this.formattingService.formatNumber((<ICatalogCartItem>item).taxPercentage * 100) : '0',
          leadTime: (<ICatalogCartItem>item).leadTime? {
            days: (<ICatalogCartItem>item).leadTime.days,
            warning: (<ICatalogCartItem>item).leadTime.warning,
            text: this.formattingService.formatBusinessDays((<ICatalogCartItem>item).leadTime.days)
          } : undefined
        }
      };

      return cartStatus;
    });

    if (util.notNullOrUndefinedOrEmpty(order.taxPercentage)) {
      this.orderEntryChargesService.taxPercentage = order.taxPercentage * 100;
      this.orderEntryChargesService.originalTaxPercentage(order.taxPercentage * 100);
    }
    order.charges = order.charges || [];
    this.orderEntryChargesService.setCharges(order.charges, this.orderEntryWebshopService.shopContext.orderPermissions.editableOwnerTypes);
  }

  public prepareOrderForSave(action?:string):IOrderUpdate {
    const newOrder:IOrderUpdate = <IOrderUpdate>{};

    // action
    util.setValue(newOrder, 'action', action, true);

    //test mode
    util.setValue(newOrder, 'isTestOrder', this.orderEntryWebshopService.testMode, true);

    // bp
    if (this.orderEntryWebshopService.selectedBP) {
      util.setValue(newOrder, 'bp', { id: this.orderEntryWebshopService.selectedBP.id });
    }

    if (action) {
      //requester
      if (this.orderEntryWebshopService.shopContext.requesterAllowed && this.orderEntryRequesterService.contact) {
        util.setValue(newOrder, 'requester', { id: this.orderEntryRequesterService.contact.id });
      }
    }

    // mail notifications
    util.setValue(newOrder, 'noMailNotifications', this._noMailNotifications, true);

    // orderCurrency
    if (this.orderEntryWebshopService.shopContext.currencyChangeAllowed) {
      util.setValue(newOrder, 'orderCurrency', { isoCode: this.priceCartService.currency }, true);
    }

    // billing type
    util.setValue(newOrder, 'billingType', this.billingTypeId, true);

    // convert selectedAddress and overwrite address object
    const copySelectedAddress:Function = (toModel:OrderEntryShippingService|OrderEntryInvoiceService) => {
      if(action && toModel.selectedAddress){ // do not overwrite when loading but only when saving or submitting
        toModel.addressData = _.clone(toModel.selectedAddress);
      }
    };

    // shipping + invoicing
    _.each([ LocationProperty.SHIP_TO, LocationProperty.INVOICE_TO ], (locType:string) => {
      const to:IObject = {};
      let toModel:OrderEntryShippingService|OrderEntryInvoiceService;
      const toLocation:IObject = {};
      if (locType === LocationProperty.SHIP_TO) {
        toModel = this.orderEntryShippingService;
        copySelectedAddress(toModel);
        if (toModel.addressData) {
          util.setValue(to, 'office', toModel.addressData.office, true);
          util.setValue(to, 'defaultAddress', toModel.addressData.defaultAddress, true);
        }
      } else if (locType === LocationProperty.INVOICE_TO) {
        toModel = this.orderEntryInvoiceService;
        copySelectedAddress(toModel);
      }

      util.setValue(to, 'addressType', toModel.selectedAddressType? toModel.selectedAddressType.id : undefined, true);

      const address:IAddressData = toModel.addressData;
      if (address) {
        util.setValue(toLocation, 'id', address.location?.id, true);
        util.setValue(toLocation, 'company', address.company? { name: address.company } : undefined, true);
        util.setValue(toLocation, 'address', address.address, true);
        util.setValue(toLocation, 'zipCode', address.zip, true);
        util.setValue(toLocation, 'city', address.city, true);
        util.setValue(toLocation, 'state', address.state? { id: address.state.id } : undefined, true);
        util.setValue(toLocation, 'country', address.country? { id: address.country.id } : undefined, true);
        util.setValue(toLocation, 'vatNumber', address.tax, true);
      }

      util.setValue(to, 'location', toLocation, true);

      util.setValue(to, 'overnightShipment', toModel.overnight, true);
      util.setValue(to, 'comment', toModel.comments, true);
      util.setValue(to, 'contactType', toModel.contactType? toModel.contactType.id : undefined, true);

      const toContact:IContact = <any>{};
      const contact:IContactModel = _.clone(toModel.selectedContact);
      if (contact) {
        util.setValue(toContact, 'id', contact.contact?.id, true);
        util.setValue(toContact, 'fullName', contact.fullName, true);
        util.setValue(toContact, 'email', contact.email, true);
        util.setValue(toContact, 'directPhone', contact.directPhone, true);

        if (locType === LocationProperty.INVOICE_TO) {
          util.setValue(toContact, 'email', toModel.eInvoiceMailAddress, false);
        }
      }

      util.setValue(to, 'contact', toContact, true);

      util.setValue(newOrder, locType, to, true);
    });

    // order info
    util.setValue(newOrder, 'orderDescription', this.orderEntryInfoService.description, true);
    util.setValue(newOrder, 'customerReference', this.orderEntryInfoService.referenceCustomer, true);
    if (this.orderEntryWebshopService.shopContext.poType === 'MANDATORY' || !this.orderEntryPaymentService.paymentType || this.orderEntryPaymentService.paymentType.id === 'PO') {
      util.setValue(newOrder, 'customerPONumber', this.orderEntryInfoService.customerPONumber, true);
    }
    if (this.orderEntryInfoService.arrivalDate) {
      const arrivalDate = parseInt(moment(this.orderEntryInfoService.arrivalDate).format('x'), 10);
      util.setValue(newOrder, 'requestedArrivalDate', arrivalDate, true);
    }
    if (this.orderEntryInfoService.orderType) {
      util.setValue(newOrder, 'orderType', { id: this.orderEntryInfoService.orderType.id });
    }
    if (this.orderEntryInfoService.costCenter) {
      util.setValue(newOrder, 'costCenter', { id: this.orderEntryInfoService.costCenter.id });
    }
    util.setValue(newOrder, 'tagValue', this.orderEntryInfoService.tagValue, true);
    util.setValue(newOrder, 'tagValue2', this.orderEntryInfoService.tagValue2, true);
    if (this.stockOrderTypeId) {
      util.setValue(newOrder, 'stockOrderType', { id: this.stockOrderTypeId });
    }
    util.setValue(newOrder, 'comment', this.orderEntryInfoService.comment, true);

    // documents
    util.setValue(newOrder, 'addDocuments', this.orderEntryDocumentsService.addedDocuments, true);
    util.setValue(newOrder, 'deleteDocuments', this.orderEntryDocumentsService.deletedDocuments, true);

    // cart
    util.setValue(newOrder, 'items', this.priceCartService.preparedCartStatus(), true);

    //leadTimeOption
    util.setValue(newOrder, 'proposeAlternatives', this.priceCartService.proposeAlternativesOption(), true);

    // payment
    if (this.orderEntryPaymentService.paymentType) {
      const payment = {};
      util.setValue(payment, 'paymentType', { id: this.orderEntryPaymentService.paymentType.id }, true);
      util.setValue(newOrder, 'payment', payment, true);
    }

    // charges
    if (util.notNullOrUndefinedOrEmpty(this.orderEntryChargesService.taxPercentage)) {
      util.setValue(newOrder, 'taxPercentage', this.orderEntryChargesService.taxPercentage / 100, true);
    }
    if (this.orderEntryChargesService.getCharges()) {
      util.setValue(newOrder, 'charges', this.orderEntryChargesService.chargesForServer(), true);
    }

    // confirmation
    if (action === ACTION_CODE_SUBMIT) {
      util.setValue(newOrder, 'acceptExtraCharges', this.orderEntryConfirmService.agreeCharges);
      util.setValue(newOrder, 'acceptAllTAndC', this.orderEntryConfirmService.agreeTC);
      util.setValue(newOrder, 'approvalReason', this.orderEntryConfirmService.approvalReason);
    }

    return newOrder;
  }

  public prepareOrderForUpdate(action?:string):IOrderUpdate {
    const newOrderQuery:IOrderUpdate = this.prepareOrderForSave(action);
    // items, shipto, invoiceto should be added entirely when there is a change.
    const fullProperties:string[] = [
      'items',
      LocationProperty.SHIP_TO,
      LocationProperty.INVOICE_TO,
      'charges'
    ];
    _.each(fullProperties, (prop:string|LocationProperty) => {
      if (!_.isEqual(this._originalOrder[ prop ], newOrderQuery[ prop ])) {
        this._originalOrder[ prop ] = null;
      }
    });

    const comparedData = jsonpatch.compare(this._originalOrder, newOrderQuery);
    const patchData = diffUtil.createPatch(comparedData);
    if (patchData.items === null) {
      patchData.items = []; // items should be an array instead of null when all items are going to be removed from the cart, otherwise the server won't see the change
    }
    if(patchData.charges === null){
      patchData.charges = []; // the server is unable to process charges when the input is null, so convert to array
    }
    return patchData;
  }

  public prepareOrderForWarnings():any {
    const warningsData = {};

    util.setValue(warningsData, 'billingType', this.billingTypeId, true);
    if (this.orderEntryPaymentService.paymentType) {
      util.setValue(warningsData, 'paymentType', this.orderEntryPaymentService.paymentType.id, true);
    }
    util.setValue(warningsData, 'orderCurrency', { isoCode: this.priceCartService.currency }, true);
    if (this.orderEntryShippingService.addressData?.country) {
      util.setValue(warningsData, LocationProperty.SHIP_TO, {
        'location': {
          'country': {
            'id': this.orderEntryShippingService.addressData.country.id
          }
        }
      });
    } else {
      util.setValue(warningsData, LocationProperty.SHIP_TO, {
        'location': { 'country': { 'id': null } }
      });
    }
    if (this.orderEntryInvoiceService.addressData?.country) {
      util.setValue(warningsData, LocationProperty.INVOICE_TO, {
        'location': {
          'country': {
            'id': this.orderEntryInvoiceService.addressData.country.id
          }
        }
      });
    } else {
      util.setValue(warningsData, LocationProperty.INVOICE_TO, {
        'location': { 'country': { 'id': null } }
      });
    }

    return warningsData;
  }

  public get orderEditable():boolean {
    return this._orderEditable;
  }
  public set orderEditable(orderEditable:boolean) {
    this._orderEditable = orderEditable;
  }

  public set noMailNotifications(noMailNotifications:boolean) {
    this._noMailNotifications = noMailNotifications;
  }

  public get orderStatus():string {
    return this._orderStatus;
  }
  public set orderStatus(orderStatus:string) {
    this._orderStatus = orderStatus;
  }

  public get orderNumber():number {
    return this._orderNumber;
  }

  public get requesterRequired():boolean {
    return this._requesterRequired;
  }
  public set requesterRequired(requesterRequired:boolean) {
    this._requesterRequired = requesterRequired;
  }

  public get checkout(): Function {
    return this._checkoutFunction;
  }
  public set checkout(checkoutFn: Function) {
    this._checkoutFunction = checkoutFn;
  }

  public get stockOrderTypeId():string {
    return this._stockOrderTypeId;
  }
  public set stockOrderTypeId(stockOrderType:string) {
    this._stockOrderTypeId = stockOrderType;
  }

  public get billingTypeId():BillingType {
    return this.orderEntryInvoiceService.billingType?.id || this.orderEntryWebshopService.shopContext.billingType;
  }

  public clearAllOrderData():void {
    this._order = undefined;
    this._orderStatus = undefined;
    this._orderNumber = undefined;
    this._originalOrder = undefined;
    this.priceCartService.clear();
    this.orderEntryLocationDataService.clear();
    this.orderEntryShippingService.clear();
    this.orderEntryInvoiceService.clear();
    this.orderEntryInfoDataService.clear();
    this.orderEntryInfoService.clear();
    this.orderEntryPaymentService.clear();
    this.orderEntryChargesService.clear();
    this.orderEntryDocumentsDataService.clear();
    this.orderEntryDocumentsService.clear();
    this.orderEntryConfirmService.clear();
  }

  public clearOrderDataWhenRequesterChanges():void {
    this.orderEntryShippingService.clear();
    this.orderEntryInvoiceService.clear();
    this.orderEntryShippingService.clear();
    this.orderEntryInvoiceService.clear();
    this.orderEntryLocationDataService.clear();
  }
}